import React, { useState } from 'react';
import { CLIENT_BY_ID } from '../../data/clientLoyalty';
import { getGenericLogoForCategory, DEFAULT_GENERIC_LOGO } from '../../utils/genericLogos';

/**
 * ClientLogo — reusable component to display a client's brand logo.
 *
 * Renders the PNG/SVG logo from `/client-logos/{clientId}.webp|svg` with a
 * graceful fallback to a brand-colored monogram if the asset is missing.
 *
 * When no client at all is provided (anonymous contracts like "Navette
 * inter-dépôts" or "Collecte e-commerce"), a generic neutral logo is
 * rendered. If a `category` prop is provided, a category-specific
 * generic logo is picked (messagerie / btp / frigorifique / vrac /
 * spécialisé / export-eu / extra-ue / rse). Otherwise the default
 * `_generic.svg` is used.
 *
 * Sizes are pixel-defined to avoid layout shift. Use the `size` prop
 * (xs/sm/md/lg/xl) or override with explicit `dimension`.
 *
 * 03/06/2026 — Client Identity overhaul + category-aware generic logos.
 */
const SIZE_PX = { xs: 20, sm: 28, md: 40, lg: 64, xl: 96 };

export default function ClientLogo({
  clientId,
  client: clientProp,
  category,
  size = 'md',
  dimension,
  className = '',
  rounded = true,
  withBorder = false,
  ...rest
}) {
  const [errored, setErrored] = useState(false);
  // Prefer explicit client prop; fall back to the registry by id.
  const client = clientProp || (clientId ? CLIENT_BY_ID[clientId] : null);
  const px = dimension || SIZE_PX[size] || SIZE_PX.md;

  // No client → render the category-specific generic logo (or default).
  if (!client) {
    const genericUrl = category ? getGenericLogoForCategory(category) : DEFAULT_GENERIC_LOGO;
    return (
      <img
        src={genericUrl}
        alt={category || 'Client anonyme'}
        title={category || 'Client anonyme'}
        loading="lazy"
        decoding="async"
        className={`object-cover ${rounded ? 'rounded-lg' : 'rounded'} ${withBorder ? 'ring-1 ring-ink-600' : ''} ${className}`}
        style={{ width: px, height: px, backgroundColor: '#ffffff' }}
        data-testid="client-logo-generic"
        {...rest}
      />
    );
  }

  const hasLogo = !!client.logoUrl && !errored;

  if (hasLogo) {
    return (
      <img
        src={client.logoUrl}
        alt={client.name}
        title={client.name}
        loading="lazy"
        decoding="async"
        className={`object-cover ${rounded ? 'rounded-lg' : 'rounded'} ${withBorder ? 'ring-1 ring-ink-600' : ''} ${className}`}
        style={{ width: px, height: px, backgroundColor: '#ffffff' }}
        onError={() => setErrored(true)}
        data-testid={`client-logo-${client.id}`}
        {...rest}
      />
    );
  }

  // Monogram fallback (brand color + 2 letters from `avatar` field).
  return (
    <div
      className={`inline-flex items-center justify-center font-display font-black text-white tracking-tight ${rounded ? 'rounded-lg' : 'rounded'} ${withBorder ? 'ring-1 ring-ink-600' : ''} ${className}`}
      style={{
        width: px,
        height: px,
        background: `linear-gradient(135deg, ${client.color}, ${client.color}cc)`,
        fontSize: Math.round(px * 0.4),
      }}
      data-testid={`client-logo-${client.id}-monogram`}
      title={client.name}
      {...rest}
    >
      {client.avatar || client.name.slice(0, 2).toUpperCase()}
    </div>
  );
}

